import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import {
  useV2GetProjectByIdQuery,
  useListProjectPricingsQuery,
  useListProjectCreditsQuery,
  useV2ListProjectServicesQuery,
  useCreatePricingAdjustmentMutation,
  useUpdatePricingAdjustmentByIdMutation,
  useDeletePricingAdjustmentByIdMutation,
  useListPricingAdjustmentsForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { formatUnprocessibleResponse } from "@utils/helperFunctions";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import Show from "./Show/Show";
import {
  PricingRow,
  PricingDetailRow,
  AdjustmentType,
} from "./types/PricingRow";

export interface AdjustModalState {
  show: boolean;
  isRecurring: boolean;
  id: string | null;
}

export interface ServiceCategoryOption {
  id: string;
  name: string;
}

interface Props {
  project_id: string;
}

const computeCategory = (
  services: any[],
  terms: number,
  adjustment: number
) => {
  const revenue = services
    .map((s) => {
      if (s.attributes["billing-frequency"] === "one_time") {
        return parseFloat(s.attributes["service-revenue"]) || 0;
      }
      return parseFloat(s.attributes.mrr) || 0;
    })
    .reduce((a, b) => a + b, 0);

  const contractAdjustment =
    adjustment *
    (services.length > 0 &&
    services[0]?.attributes["billing-frequency"] !== "one_time"
      ? terms
      : 1);

  const net = revenue + adjustment;

  const tcv =
    services
      .map((s) => parseFloat(s.attributes["contract-revenue"]) || 0)
      .reduce((a, b) => a + b, 0) + contractAdjustment;

  const cost = services
    .map((s) => parseFloat(s.attributes["contract-cost"]) || 0)
    .reduce((a, b) => a + b, 0);

  const profit = tcv - cost;
  const margin = tcv !== 0 ? (profit / tcv) * 100 : null;

  return { revenue, adjustment, net, tcv, cost, profit, margin };
};

interface ServiceInfo {
  id: string;
  name: string;
  subserviceIds: Set<string>;
}

const buildServiceHierarchy = (
  servicesData: any[],
  includedData: any[]
): ServiceInfo[] => {
  const subserviceNames: Record<string, string> = {};
  for (const item of includedData) {
    if (item.type === "project-subservices") {
      subserviceNames[String(item.id)] = item.attributes?.name || "";
    }
  }

  return servicesData.map((svc) => {
    const subIds = new Set<string>();
    const subRels = svc.relationships?.["project-subservices"]?.data;
    if (Array.isArray(subRels)) {
      for (const rel of subRels) {
        subIds.add(String(rel.id));
      }
    }
    return {
      id: String(svc.id),
      name: svc.attributes?.name || "",
      subserviceIds: subIds,
    };
  });
};

const aggregatePricings = (
  pricings: any[]
): Omit<PricingDetailRow, "id" | "source"> => {
  const revenue = pricings
    .map((p) => {
      if (p.attributes["billing-frequency"] === "one_time") {
        return parseFloat(p.attributes["service-revenue"]) || 0;
      }
      return parseFloat(p.attributes.mrr) || 0;
    })
    .reduce((a, b) => a + b, 0);

  const tcv = pricings
    .map((p) => parseFloat(p.attributes["contract-revenue"]) || 0)
    .reduce((a, b) => a + b, 0);

  const cost = pricings
    .map((p) => parseFloat(p.attributes["contract-cost"]) || 0)
    .reduce((a, b) => a + b, 0);

  const profit = tcv - cost;
  const margin = tcv !== 0 ? (profit / tcv) * 100 : null;

  return { revenue, tcv, cost, profit, margin };
};

const buildGroupedDetailRows = (
  pricings: any[],
  services: ServiceInfo[]
): PricingDetailRow[] => {
  const details: PricingDetailRow[] = [];
  const matchedIds = new Set<string>();

  for (const svc of services) {
    const matchingPricings = pricings.filter((p) => {
      const sourceType = p.attributes["source-type"];
      const sourceId = String(p.attributes["source-id"]);
      if (sourceType === "ProjectService" && sourceId === svc.id) return true;
      if (sourceType === "ProjectSubservice" && svc.subserviceIds.has(sourceId))
        return true;
      return false;
    });

    if (matchingPricings.length === 0) continue;

    for (const p of matchingPricings) {
      matchedIds.add(String(p.id));
    }

    const agg = aggregatePricings(matchingPricings);
    const servicePricing = matchingPricings.find(
      (p) => p.attributes["source-type"] === "ProjectService"
    );
    const name = servicePricing?.attributes.name || svc.name;

    details.push({
      id: `svc-${svc.id}`,
      source: name,
      ...agg,
    });
  }

  const unmatchedPricings = pricings.filter(
    (p) => !matchedIds.has(String(p.id))
  );
  for (const p of unmatchedPricings) {
    const attr = p.attributes;
    const name = attr.name || attr["resource-name"] || String(p.id);
    const agg = aggregatePricings([p]);

    details.push({
      id: String(p.id),
      source: name,
      ...agg,
    });
  }

  return details;
};

const buildFlatDetailRows = (
  pricings: any[],
  {
    isMaterials = false,
    isAdjustment = false,
    adjustmentMap = {} as Record<string, string | undefined>,
  } = {}
): PricingDetailRow[] =>
  pricings.map((s) => {
    const attr = s.attributes;
    const name = attr.name || attr["resource-name"] || String(s.id);

    let revenue: number;
    let tcv: number;
    let cost: number;

    if (isMaterials) {
      revenue = parseFloat(attr["hardware-price"]) || 0;
      tcv = revenue;
      cost = parseFloat(attr["hardware-cost"]) || 0;
    } else {
      revenue =
        attr["billing-frequency"] === "one_time"
          ? parseFloat(attr["service-revenue"]) || 0
          : parseFloat(attr.mrr) || 0;
      tcv = parseFloat(attr["contract-revenue"]) || 0;
      cost = parseFloat(attr["contract-cost"]) || 0;
    }

    const profit = tcv - cost;
    const margin = tcv !== 0 ? (profit / tcv) * 100 : null;

    const adjustmentId = isAdjustment ? String(s.id).split("-")[2] : undefined;

    return {
      id: adjustmentId || String(s.id),
      source: name,
      revenue,
      tcv,
      cost,
      profit,
      margin,
      ...(isAdjustment && {
        editPrice: String(attr["service-revenue"] ?? ""),
        editCost: String(attr["service-cost"] ?? ""),
        billingFrequency: attr["billing-frequency"],
        serviceCategoryId: adjustmentId
          ? adjustmentMap[adjustmentId]
          : undefined,
      }),
    };
  });

const ProjectPricingV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const authToken = useSelector((state: RootState) => state.auth.token);

  const { data: project, isLoading: projectLoading } = useV2GetProjectByIdQuery({
    id: String(projectId),
    include: ["project-services.service-category"] as any,
  });

  const {
    data: pricingsData,
    isLoading: pricingsLoading,
    refetch: refetchPricings,
  } = useListProjectPricingsQuery({
    slug: accountSlug,
    id: Number(projectId),
  });

  const { data: creditsData, isLoading: creditsLoading } =
    useListProjectCreditsQuery({
      slug: accountSlug,
      filter: { project: projectId, active: true },
    });

  const { data: adjustmentsData } = useListPricingAdjustmentsForAccountQuery({
    slug: accountSlug,
    projectId: Number(projectId),
  });

  const { data: servicesData, isLoading: servicesLoading } =
    useV2ListProjectServicesQuery({
      filter: { project: projectId },
      include: ["project-subservices"],
      page: { size: 500 },
    });

  const authorized = useAuthorization();
  const { hasPrivilege } = usePrivilegeCheck();
  const canManagePricingAdjustments = hasPrivilege(
    "projects.pricing_adjustment",
    ["manage"]
  );

  const [createPricingAdjustment] = useCreatePricingAdjustmentMutation();
  const [updatePricingAdjustment] = useUpdatePricingAdjustmentByIdMutation();
  const [deletePricingAdjustment] = useDeletePricingAdjustmentByIdMutation();

  const [showMarginModal, setShowMarginModal] = useState(false);
  const [projectMargin, setProjectMargin] = useState("");
  const [marginError, setMarginError] = useState<string[] | null>(null);

  const [adjustModal, setAdjustModal] = useState<AdjustModalState>({
    show: false,
    isRecurring: false,
    id: null,
  });
  const [adjustPrice, setAdjustPrice] = useState("");
  const [adjustCost, setAdjustCost] = useState("");
  const [adjustRepeats, setAdjustRepeats] = useState("select");
  const [adjustServiceCategory, setAdjustServiceCategory] = useState("select");
  const [adjustError, setAdjustError] = useState<string[] | null>(null);

  const handleDeleteAdjustments = useCallback(
    async (ids: string[]) => {
      const promises = ids.map((id) =>
        deletePricingAdjustment({
          slug: accountSlug,
          projectId: Number(projectId),
          id: Number(id),
        })
      );
      await Promise.all(promises);
      refetchPricings();
    },
    [accountSlug, projectId, deletePricingAdjustment, refetchPricings]
  );

  const handleApplyMargin = useCallback(async () => {
    const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
    const data = {
      data: {
        attributes: {
          "contract-margin": Number((Number(projectMargin) / 100).toFixed(2)),
        },
      },
    };

    try {
      await fetch(
        `${apiHost}/${accountSlug}/v1/projects/${projectId}/set_margin`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(data),
        }
      ).then((res) => {
        if (!res.ok) throw res;
        return res.json();
      });
      setShowMarginModal(false);
      refetchPricings();
    } catch (err: any) {
      setMarginError(
        formatUnprocessibleResponse(
          err,
          "Project margin"
        ) as unknown as string[]
      );
    }
  }, [projectMargin, accountSlug, projectId, authToken, refetchPricings]);

  const resetAdjustFields = () => {
    setAdjustPrice("");
    setAdjustCost("");
    setAdjustRepeats("select");
    setAdjustServiceCategory("select");
    setAdjustError(null);
  };

  const handleOpenAdjustModal = useCallback(
    (adjustmentType: AdjustmentType) => {
      resetAdjustFields();
      setAdjustModal({
        show: true,
        isRecurring: adjustmentType === "recurring",
        id: null,
      });
    },
    []
  );

  const handleEditAdjustment = useCallback((detail: PricingDetailRow) => {
    setAdjustPrice(detail.editPrice || "");
    setAdjustCost(detail.editCost || "");
    setAdjustRepeats(
      detail.billingFrequency !== "one_time"
        ? detail.billingFrequency || "select"
        : "select"
    );
    setAdjustServiceCategory(detail.serviceCategoryId || "select");
    setAdjustError(null);
    setAdjustModal({
      show: true,
      isRecurring: detail.billingFrequency !== "one_time",
      id: detail.id,
    });
  }, []);

  const handleCancelAdjust = useCallback(() => {
    setAdjustModal({ show: false, isRecurring: false, id: null });
    resetAdjustFields();
  }, []);

  const handleApplyAdjustment = useCallback(async () => {
    const body: any = {
      data: {
        type: "pricing_adjustments",
        attributes: {
          price: adjustPrice,
          cost: adjustCost,
          "billing-frequency": adjustModal.isRecurring
            ? adjustRepeats
            : "one_time",
          "source-type":
            adjustServiceCategory !== "select" ? "service-category" : "project",
          "source-id":
            adjustServiceCategory !== "select"
              ? adjustServiceCategory
              : projectId,
        },
        relationships: {
          project: {
            data: { type: "projects", id: projectId },
          },
        },
      },
    };

    try {
      if (adjustModal.id !== null) {
        body.data.id = adjustModal.id;
        await updatePricingAdjustment({
          slug: accountSlug,
          projectId: Number(projectId),
          id: Number(adjustModal.id),
          body,
        }).unwrap();
      } else {
        await createPricingAdjustment({
          slug: accountSlug,
          projectId: Number(projectId),
          body,
        }).unwrap();
      }
      setAdjustModal({ show: false, isRecurring: false, id: null });
      resetAdjustFields();
      refetchPricings();
    } catch (err: any) {
      setAdjustError(
        formatUnprocessibleResponse(
          err,
          "Revenue details"
        ) as unknown as string[]
      );
    }
  }, [
    adjustModal,
    adjustPrice,
    adjustCost,
    adjustRepeats,
    adjustServiceCategory,
    accountSlug,
    projectId,
    createPricingAdjustment,
    updatePricingAdjustment,
    refetchPricings,
  ]);

  if (
    !authorized ||
    projectLoading ||
    pricingsLoading ||
    creditsLoading ||
    servicesLoading ||
    !project
  ) {
    return <ScopeStackSpinner />;
  }

  const attr = (project?.data?.attributes as any) || {};
  const currencyUnit = attr["field-labels"]?.currency_unit || "$";
  const fieldLabels: Record<string, string> = attr["field-labels"] || {};
  const terms = parseFloat(attr["mrr-terms"]) || 0;
  const includeHardware = attr["payment-info"]?.["include-product"] || false;

  const allPricings: any[] = (pricingsData?.data as any[]) || [];
  const allServiceRecords: any[] = (servicesData?.data as any[]) || [];
  const includedRecords: any[] = (servicesData?.included as any[]) || [];

  const profServiceRecords = allServiceRecords.filter(
    (s) =>
      s.attributes["service-type"] === "professional_services" &&
      s.attributes["task-source"] !== "third_party"
  );
  const profOneTimeServices = profServiceRecords.filter(
    (s) => s.attributes["payment-frequency"] !== "amortized"
  );
  const profAmortizedServices = profServiceRecords.filter(
    (s) => s.attributes["payment-frequency"] === "amortized"
  );
  const managedServiceRecords = allServiceRecords.filter(
    (s) =>
      s.attributes["service-type"] === "managed_services" &&
      s.attributes["task-source"] !== "third_party"
  );

  const profOneTimeHierarchy = buildServiceHierarchy(
    profOneTimeServices,
    includedRecords
  );
  const profAmortizedHierarchy = buildServiceHierarchy(
    profAmortizedServices,
    includedRecords
  );
  const managedHierarchy = buildServiceHierarchy(
    managedServiceRecords,
    includedRecords
  );

  const professionalPricings = allPricings.filter(
    (s) =>
      s.attributes["service-type"] === "professional" &&
      s.attributes["billing-frequency"] === "one_time"
  );

  const amortizedPricings = allPricings.filter(
    (s) =>
      s.attributes["service-type"] === "professional" &&
      s.attributes["billing-frequency"] !== "one_time"
  );

  const managedPricings = allPricings.filter(
    (s) => s.attributes["service-type"] === "managed"
  );

  const thirdPartyPricings = allPricings.filter(
    (s) =>
      s.attributes["source-type"] === "Quote" ||
      s.attributes["source-type"] === "ThirdPartyRequest"
  );

  const oneTimeThirdParty = thirdPartyPricings.filter(
    (s) => s.attributes["billing-frequency"] === "one_time"
  );

  const recurringThirdParty = thirdPartyPricings.filter(
    (s) => s.attributes["billing-frequency"] !== "one_time"
  );

  const materialsPricings = allPricings.filter(
    (s) => s.attributes["service-type"] === "materials"
  );

  const oneTimeAdjustments = allPricings.filter(
    (s) =>
      s.attributes["service-type"] === "adjustments" &&
      s.attributes["billing-frequency"] === "one_time"
  );

  const recurringAdjustments = allPricings.filter(
    (s) =>
      s.attributes["service-type"] === "adjustments" &&
      s.attributes["billing-frequency"] !== "one_time"
  );

  const adjustmentRecords: any[] = (adjustmentsData?.data as any[]) || [];
  const adjustmentMap: Record<string, string | undefined> = {};
  for (const adj of adjustmentRecords) {
    const attrs = adj.attributes || {};
    const srcType =
      attrs["source-type"] || attrs["source_type"] || attrs.source_type;
    const srcId = attrs["source-id"] ?? attrs["source_id"] ?? attrs.source_id;
    if (srcType === "Section" && srcId != null) {
      adjustmentMap[String(adj.id)] = String(srcId);
    }
  }

  const prof = computeCategory(professionalPricings, terms, 0);
  const amort = computeCategory(amortizedPricings, terms, 0);
  const managed = computeCategory(managedPricings, terms, 0);
  const otThirdParty = computeCategory(oneTimeThirdParty, terms, 0);
  const recThirdParty = computeCategory(recurringThirdParty, terms, 0);
  const otAdj = computeCategory(oneTimeAdjustments, terms, 0);
  const recAdj = computeCategory(recurringAdjustments, terms, 0);

  const oneTimeHasRevenue = prof.revenue !== 0 || otThirdParty.revenue !== 0;
  const recurringHasRevenue =
    managed.revenue !== 0 || recThirdParty.revenue !== 0;
  const canAdjustOneTime = canManagePricingAdjustments && oneTimeHasRevenue;
  const canAdjustRecurring = canManagePricingAdjustments && recurringHasRevenue;

  const productRevenue = materialsPricings
    .map((s) => parseFloat(s.attributes["hardware-price"]) || 0)
    .reduce((a, b) => a + b, 0);
  const productCost = materialsPricings
    .map((s) => parseFloat(s.attributes["hardware-cost"]) || 0)
    .reduce((a, b) => a + b, 0);
  const productProfit = productRevenue - productCost;
  const productMargin =
    productRevenue !== 0 ? (productProfit / productRevenue) * 100 : null;

  const rows: PricingRow[] = [];

  rows.push({
    id: "section-one-time",
    source: "One Time Revenue",
    revenue: 0,
    adjustment: 0,
    net: 0,
    tcv: 0,
    cost: 0,
    profit: 0,
    margin: null,
    rowType: "section",
  });

  rows.push({
    id: "professional",
    source: "Professional Services",
    revenue: prof.revenue,
    adjustment: prof.adjustment,
    net: prof.net,
    tcv: prof.tcv,
    cost: prof.cost,
    profit: prof.profit,
    margin: prof.margin,
    rowType: "data",
    detailRows: buildGroupedDetailRows(
      professionalPricings,
      profOneTimeHierarchy
    ),
  });

  rows.push({
    id: "third-party-ot",
    source: "Third Party Services",
    revenue: otThirdParty.revenue,
    adjustment: otThirdParty.adjustment,
    net: otThirdParty.net,
    tcv: otThirdParty.tcv,
    cost: otThirdParty.cost,
    profit: otThirdParty.profit,
    margin: otThirdParty.margin,
    rowType: "data",
    detailRows: buildFlatDetailRows(oneTimeThirdParty),
  });

  rows.push({
    id: "adj-ot",
    source: "Pricing Adjustments",
    revenue: otAdj.revenue,
    adjustment: otAdj.adjustment,
    net: otAdj.net,
    tcv: otAdj.tcv,
    cost: otAdj.cost,
    profit: otAdj.profit,
    margin: otAdj.margin,
    rowType: "data",
    detailRows: buildFlatDetailRows(oneTimeAdjustments, {
      isAdjustment: true,
      adjustmentMap,
    }),
    adjustmentType: canAdjustOneTime ? "one_time" : undefined,
  });

  rows.push({
    id: "section-recurring",
    source: "Recurring Revenue",
    revenue: 0,
    adjustment: 0,
    net: 0,
    tcv: 0,
    cost: 0,
    profit: 0,
    margin: null,
    rowType: "section",
  });

  if (amortizedPricings.length > 0) {
    rows.push({
      id: "amortized",
      source: "Amortized Professional Services",
      revenue: amort.revenue,
      adjustment: amort.adjustment,
      net: amort.net,
      tcv: amort.tcv,
      cost: amort.cost,
      profit: amort.profit,
      margin: amort.margin,
      rowType: "data",
      detailRows: buildGroupedDetailRows(
        amortizedPricings,
        profAmortizedHierarchy
      ),
    });
  }

  rows.push({
    id: "managed",
    source: "Managed Services",
    revenue: managed.revenue,
    adjustment: managed.adjustment,
    net: managed.net,
    tcv: managed.tcv,
    cost: managed.cost,
    profit: managed.profit,
    margin: managed.margin,
    rowType: "data",
    detailRows: buildGroupedDetailRows(managedPricings, managedHierarchy),
  });

  rows.push({
    id: "third-party-rec",
    source: "Third Party Services",
    revenue: recThirdParty.revenue,
    adjustment: recThirdParty.adjustment,
    net: recThirdParty.net,
    tcv: recThirdParty.tcv,
    cost: recThirdParty.cost,
    profit: recThirdParty.profit,
    margin: recThirdParty.margin,
    rowType: "data",
    detailRows: buildFlatDetailRows(recurringThirdParty),
  });

  rows.push({
    id: "adj-rec",
    source: "Pricing Adjustments",
    revenue: recAdj.revenue,
    adjustment: recAdj.adjustment,
    net: recAdj.net,
    tcv: recAdj.tcv,
    cost: recAdj.cost,
    profit: recAdj.profit,
    margin: recAdj.margin,
    rowType: "data",
    detailRows: buildFlatDetailRows(recurringAdjustments, {
      isAdjustment: true,
      adjustmentMap,
    }),
    adjustmentType: canAdjustRecurring ? "recurring" : undefined,
  });

  if (includeHardware) {
    rows.push({
      id: "section-product",
      source: "Product",
      revenue: 0,
      adjustment: 0,
      net: 0,
      tcv: 0,
      cost: 0,
      profit: 0,
      margin: null,
      rowType: "section",
    });

    rows.push({
      id: "product",
      source: "Product",
      revenue: productRevenue,
      adjustment: 0,
      net: productRevenue,
      tcv: productRevenue,
      cost: productCost,
      profit: productProfit,
      margin: productMargin,
      rowType: "data",
      detailRows: buildFlatDetailRows(materialsPricings, { isMaterials: true }),
    });
  }

  const sumField = (field: keyof PricingRow) =>
    rows
      .filter((r) => r.rowType === "data")
      .reduce((acc, r) => acc + (Number(r[field]) || 0), 0);

  const totalTcv = sumField("tcv");
  const totalCost = sumField("cost");
  const totalProfit = sumField("profit");
  const totalMargin = totalTcv !== 0 ? (totalProfit / totalTcv) * 100 : 0;

  const credits = (creditsData?.data as any[]) || [];
  const paymentCreditsTotal =
    credits.length > 0
      ? credits
          .map((c) => parseFloat(c.attributes?.amount) || 0)
          .reduce((a, b) => a + b, 0)
      : null;
  const customerPaymentsTotal =
    paymentCreditsTotal !== null ? totalTcv - paymentCreditsTotal : null;

  const currentContractMargin = parseFloat(attr["contract-margin"]) || 0;
  const currentMarginPct = (currentContractMargin * 100).toFixed(2);

  const handleOpenMarginModal = () => {
    setProjectMargin(currentMarginPct);
    setMarginError(null);
    setShowMarginModal(true);
  };

  const handleCancelMargin = () => {
    setShowMarginModal(false);
    setProjectMargin(currentMarginPct);
    setMarginError(null);
  };

  const projectIncluded: any[] = ((project as any)?.included as any[]) || [];
  const serviceCategories: ServiceCategoryOption[] = projectIncluded
    .filter((item) => item.type === "service-categories")
    .map((item) => ({
      id: String(item.id),
      name:
        item.attributes?.["nested-name"] ||
        item.attributes?.name ||
        String(item.id),
    }));

  return (
    <Show
      rows={rows}
      currencyUnit={currencyUnit}
      isLoading={pricingsLoading}
      totalTcv={totalTcv}
      totalCost={totalCost}
      totalProfit={totalProfit}
      totalMargin={totalMargin}
      paymentCreditsTotal={paymentCreditsTotal}
      customerPaymentsTotal={customerPaymentsTotal}
      showMarginModal={showMarginModal}
      onOpenMarginModal={handleOpenMarginModal}
      projectMargin={projectMargin}
      setProjectMargin={setProjectMargin}
      marginError={marginError}
      onApplyMargin={handleApplyMargin}
      onCancelMargin={handleCancelMargin}
      serviceCategories={serviceCategories}
      adjustModal={adjustModal}
      adjustPrice={adjustPrice}
      setAdjustPrice={setAdjustPrice}
      adjustCost={adjustCost}
      setAdjustCost={setAdjustCost}
      adjustRepeats={adjustRepeats}
      setAdjustRepeats={setAdjustRepeats}
      adjustServiceCategory={adjustServiceCategory}
      setAdjustServiceCategory={setAdjustServiceCategory}
      adjustError={adjustError}
      onOpenAdjustModal={handleOpenAdjustModal}
      onApplyAdjustment={handleApplyAdjustment}
      onCancelAdjust={handleCancelAdjust}
      canManagePricingAdjustments={canManagePricingAdjustments}
      onDeleteAdjustments={handleDeleteAdjustments}
      onEditAdjustment={handleEditAdjustment}
      fieldLabels={fieldLabels}
    />
  );
};

export default ProjectPricingV2Mspa;
